import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, TextField, MenuItem, IconButton, Tooltip,
  CircularProgress, Chip, Button,
} from '@mui/material';
import { Add, Delete, Edit, Close, InfoOutlined } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import ConfirmModal from './modals/ConfirmModal';
import SavingOverlay from './SavingOverlay';
import { Toast } from './Toast';
import { useToast } from '../hooks/useToast';
import ResultCount from './ResultCount';
import {
  patientDataAccessService, dataAccessErrorMessage,
  DataAccessRow, ProfessionalOption,
} from '../services/patientDataAccessService';
import { colors, typography } from '../theme/designSystem';
import { filterFieldSx, dateFieldSx, filterActionButtonSx } from '../theme/filterStyles';

/**
 * Acesso a dados do paciente (RN-540a). Substitui a tela "Supervisor Pacientes" do Bubble.
 *
 * O nome mudou de propósito: a relação não descreve supervisão, e sim quem pode ver os dados
 * de quem, naquele paciente, naquele período. A EXISTÊNCIA do registro clínico é sempre
 * visível a quem atende; é o CONTEÚDO que depende desta concessão.
 *
 * Ampliação sobre o Bubble (decisão do Hilton, 02/09): os combos listam TODOS os profissionais
 * ativos, não só quem atende o paciente — um supervisor costuma justamente não atender, e a
 * regra antiga deixava de fora o caso mais comum.
 */
interface Props {
  patientId: string;
}

/**
 * Conceder e encerrar acesso é do ADMINISTRADOR (RN-540b). Quem atende o paciente vê a lista —
 * precisa saber quem enxerga o quê —, mas com o formulário travado: campo aberto que o servidor
 * vai recusar é convite para a pessoa perder tempo preenchendo.
 */

const SITUACAO: Record<string, { label: string; bg: string; color: string }> = {
  vigente: { label: 'Vigente', bg: '#d4edda', color: '#155724' },
  agendado: { label: 'Agendado', bg: '#e3f2fd', color: '#0d47a1' },
  vencido: { label: 'Vencido', bg: '#f8d7da', color: '#721c24' },
};

const hojeIso = (): string => new Date().toISOString().slice(0, 10);
const emUmAnoIso = (): string => {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
};

const formatarData = (iso: string | null): string => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

const PatientDataAccessTab: React.FC<Props> = ({ patientId }) => {
  const { user } = useAuth();
  const podeAdministrar = (user?.allowedPages || []).includes('patient-data-access');
  const [items, setItems] = useState<DataAccessRow[]>([]);
  const [professionals, setProfessionals] = useState<ProfessionalOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [supervisorId, setSupervisorId] = useState('');
  const [supervisedId, setSupervisedId] = useState('');
  const [initialDate, setInitialDate] = useState(hojeIso());
  const [finalDate, setFinalDate] = useState(emUmAnoIso());
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<DataAccessRow | null>(null);
  const [deleting, setDeleting] = useState(false);

  const { toast, showToast, hideToast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await patientDataAccessService.getByPatient(patientId);
      setItems(data.items);
      setProfessionals(data.professionals);
    } catch {
      setError('Não foi possível carregar os acessos deste paciente.');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => { load(); }, [load]);

  const limparForm = () => {
    setEditingId(null);
    setSupervisorId('');
    setSupervisedId('');
    setInitialDate(hojeIso());
    setFinalDate(emUmAnoIso());
    setFormError(null);
  };

  const editar = (row: DataAccessRow) => {
    setEditingId(row.id);
    setSupervisorId(row.supervisorId);
    setSupervisedId(row.supervisedId);
    setInitialDate(row.initialDate ?? hojeIso());
    setFinalDate(row.finalDate ?? emUmAnoIso());
    setFormError(null);
  };

  const salvar = async () => {
    setFormError(null);
    if (!supervisorId || !supervisedId) {
      setFormError('Escolha quem vê e de quem os dados serão vistos.');
      return;
    }
    setSaving(true);
    try {
      await patientDataAccessService.save({
        id: editingId ?? undefined,
        patientId,
        supervisorId,
        supervisedId,
        initialDate: initialDate || null,
        finalDate: finalDate || null,
      });
      showToast(editingId ? 'Acesso atualizado.' : 'Acesso concedido.', 'success');
      limparForm();
      await load();
    } catch (e) {
      setFormError(dataAccessErrorMessage(e, 'Não foi possível salvar o acesso.'));
    } finally {
      setSaving(false);
    }
  };

  const excluir = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await patientDataAccessService.remove(deleteTarget.id);
      setDeleteTarget(null);
      showToast('Acesso encerrado.', 'success');
      await load();
    } catch (e) {
      showToast(dataAccessErrorMessage(e, 'Não foi possível encerrar o acesso.'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const nomeCom = (p: ProfessionalOption) => `${p.name}${p.attends ? '' : ' · não atende'}`;

  return (
    <div className="tab-content-section">
      {/* Formulário de concessão: os combos trazem TODOS os ativos, com marca de quem
          não atende o paciente — o supervisor costuma ser justamente alguém de fora. */}
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
        <TextField
          select
          size="small"
          label="Quem vê"
          value={supervisorId}
          onChange={e => setSupervisorId(e.target.value)}
          disabled={!podeAdministrar}
          InputLabelProps={{ shrink: true }}
          sx={filterFieldSx}
        >
          <MenuItem value="">Selecione</MenuItem>
          {professionals.map(p => (
            <MenuItem key={p.id} value={p.id}>{nomeCom(p)}</MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Dados de quem"
          value={supervisedId}
          onChange={e => setSupervisedId(e.target.value)}
          disabled={!podeAdministrar}
          InputLabelProps={{ shrink: true }}
          sx={filterFieldSx}
        >
          <MenuItem value="">Selecione</MenuItem>
          {professionals.map(p => (
            <MenuItem key={p.id} value={p.id}>{nomeCom(p)}</MenuItem>
          ))}
        </TextField>

        <TextField
          size="small"
          type="date"
          label="De"
          value={initialDate}
          onChange={e => setInitialDate(e.target.value)}
          disabled={!podeAdministrar}
          InputLabelProps={{ shrink: true }}
          sx={dateFieldSx}
        />
        <TextField
          size="small"
          type="date"
          label="Até"
          value={finalDate}
          onChange={e => setFinalDate(e.target.value)}
          disabled={!podeAdministrar}
          InputLabelProps={{ shrink: true }}
          sx={dateFieldSx}
        />

        {editingId && (
          <Tooltip title="Cancelar edição">
            <IconButton onClick={limparForm} sx={{ bgcolor: '#e9ecef', color: '#6c757d', width: 40, height: 40 }}>
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={
          podeAdministrar
            ? (editingId ? 'Salvar alteração' : 'Conceder acesso')
            : 'Só o Administrador concede acesso'
        }>
          <span>
            <IconButton
              onClick={salvar}
              disabled={saving || loading || !podeAdministrar}
              sx={filterActionButtonSx}
            >
              {editingId ? <Edit /> : <Add />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {formError && (
        <Typography sx={{ color: colors.error, fontSize: typography.fontSize.sm, mb: 2 }}>
          {formError}
        </Typography>
      )}

      <Box className="c4u-info-box">
        <InfoOutlined className="c4u-info-box__icon" sx={{ fontSize: "1.1rem" }} />
        <Typography sx={{ fontSize: typography.fontSize.xs, color: colors.textPrimary }}>
          Só o <strong>Administrador</strong> concede e encerra acesso. Quem atende o paciente sempre <strong>vê que o registro existe</strong>. O acesso concedido
          aqui é o que permite <strong>abrir o conteúdo</strong> — respostas, parecer e texto clínico —
          e vale apenas no período informado.
        </Typography>
      </Box>

      {loading
        ? <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.textSecondary, mb: 1 }}>Carregando...</Typography>
        : <ResultCount shown={items.length} />}

      {error && (
        <Typography sx={{ color: colors.error, fontSize: typography.fontSize.sm, mb: 2 }}>{error}</Typography>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} sx={{ color: colors.primary }} />
        </Box>
      ) : items.length === 0 ? (
        <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.textSecondary, py: 3, textAlign: 'center' }}>
          Nenhum acesso concedido. Sem isso, cada profissional abre apenas o que ele mesmo registrou.
        </Typography>
      ) : (
        <Box sx={{ backgroundColor: '#fff', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
          {/* Cabeçalho: toda lista tem um, e cada dado fica na sua coluna */}
          <Box className="c4u-table__header">
            <Box sx={{ flex: '1 1 160px', minWidth: 0 }}>Quem vê</Box>
            <Box sx={{ flex: '1 1 160px', minWidth: 0 }}>Dados de</Box>
            <Box sx={{ flex: '0 0 100px' }}>Situação</Box>
            <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: '0 0 100px' }}>De</Box>
            <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: '0 0 100px' }}>Até</Box>
            <Box sx={{ flex: '0 0 80px', textAlign: 'right' }}>Ações</Box>
          </Box>

          {items.map(row => {
            const s = SITUACAO[row.situation] ?? SITUACAO.vigente;
            return (
              <Box key={row.id} className="c4u-table__row">
                <Box sx={{ flex: '1 1 160px', minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: colors.text }}>
                    {row.supervisorName}
                  </Typography>
                  {!row.supervisorAttends && (
                    <Typography sx={{ fontSize: '0.72rem', color: colors.textSecondary }}>
                      não atende este paciente
                    </Typography>
                  )}
                </Box>
                <Typography sx={{ flex: '1 1 160px', minWidth: 0, fontWeight: 600, fontSize: '0.875rem', color: colors.text }}>
                  {row.supervisedName}
                </Typography>
                <Box sx={{ flex: '0 0 100px' }}>
                  <Chip
                    label={s.label}
                    size="small"
                    sx={{ height: 22, fontSize: '0.72rem', backgroundColor: s.bg, color: s.color }}
                  />
                </Box>
                <Typography className="admin-plans-cell c4u-col--md" data-label="De" sx={{ flex: '0 0 100px', fontSize: '0.85rem', color: colors.textSecondary }}>
                  {formatarData(row.initialDate)}
                </Typography>
                <Typography className="admin-plans-cell c4u-col--md" data-label="Até" sx={{ flex: '0 0 100px', fontSize: '0.85rem', color: colors.textSecondary }}>
                  {formatarData(row.finalDate)}
                </Typography>
                <Box sx={{ flex: '0 0 80px', display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                  <Tooltip title="Editar período">
                    <IconButton size="small" onClick={() => editar(row)} disabled={!podeAdministrar} sx={{ color: colors.primary }}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Encerrar acesso">
                    <IconButton size="small" onClick={() => setDeleteTarget(row)} disabled={!podeAdministrar} sx={{ color: colors.error }}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      <ConfirmModal
        isOpen={deleteTarget !== null}
        title="Encerrar acesso"
        message={`${deleteTarget?.supervisorName} deixa de abrir o conteúdo registrado por ${deleteTarget?.supervisedName} neste paciente. O histórico de quem teve acesso permanece.`}
        confirmButtonText="Encerrar"
        cancelButtonText="Cancelar"
        type="danger"
        onConfirm={excluir}
        onCancel={() => setDeleteTarget(null)}
        isProcessing={deleting}
      />

      <SavingOverlay open={saving} message="Salvando acesso..." />
      <Toast isVisible={toast.isVisible} message={toast.message} type={toast.type} onClose={hideToast} />
    </div>
  );
};

export default PatientDataAccessTab;
